import { css, type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

export type TextStypeProps = {
  color?: string;
  fontFamily?: string;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
};

const h1 = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.f8};
  line-height: ${({ theme }) => theme.lineHeights.sm};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f10};
    line-height: ${({ theme }) => theme.lineHeights.sm};
  }
`;

const h2 = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  font-size: ${({ theme }) => theme.fontSizes.f6};
  line-height: ${({ theme }) => theme.lineHeights.md};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f9};
    line-height: ${({ theme }) => theme.lineHeights.md};
  }
`;

const h3 = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.f6};
  line-height: ${({ theme }) => theme.lineHeights.md};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f7};
    line-height: ${({ theme }) => theme.lineHeights.md};
  }
`;

const h4 = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.f4};
  line-height: ${({ theme }) => theme.lineHeights.xl};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f6};
    line-height: ${({ theme }) => theme.lineHeights.md};
  }
`;

const b1 = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.f4};
  line-height: ${({ theme }) => theme.lineHeights.xl};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f6};
    line-height: ${({ theme }) => theme.lineHeights.lg};
  }
`;

const b2 = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.f3};
  line-height: ${({ theme }) => theme.lineHeights.xl};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f4};
    line-height: ${({ theme }) => theme.lineHeights.lg};
  }
`;

const c1 = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.f2};
  line-height: ${({ theme }) => theme.lineHeights.xl};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f3};
    line-height: ${({ theme }) => theme.lineHeights.xl};
  }
`;

const c2 = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.f1};
  line-height: ${({ theme }) => theme.lineHeights.xl};

  ${({ theme }) => theme.media.up('sm')} {
    font-size: ${({ theme }) => theme.fontSizes.f2};
    line-height: ${({ theme }) => theme.lineHeights.xl};
  }
`;

const grey = styled.p`
  color: ${({ theme }) => theme.palette.colors.gray['400']};
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.f2};
  a {
    text-decoration: underline;
    color: inherit;
  }
`;

export const textStyles = (styles: TextStypeProps): SerializedStyles =>
  css`
    ${styles.color && `color: ${styles.color};`}
    ${styles.fontFamily && `font-family: ${styles.fontFamily};`}
    ${styles.fontSize && `font-size: ${styles.fontSize} !important;`}
    ${styles.fontWeight && `font-weight: ${styles.fontWeight} !important;`}
    ${styles.lineHeight && `line-height: ${styles.lineHeight} !important;`}
  `;

export const styledComponents = {
  h1,
  h2,
  h3,
  h4,
  b1,
  b2,
  c1,
  c2,
  grey,
};
